import { LitElement, html, css } from 'https://cdn.jsdelivr.net/gh/lit/dist@2/core/lit-core.min.js';

// Selling plan IDs to hide from the picker (e.g. free month offers)
const HIDDEN_SELLING_PLAN_IDS = [6080299196, 6080266428, 6105989308];

/**
 * KOS Skio Plan Picker
 * Styled to match KOS design system
 */
const skioStyles = css`
  :host {
    display: block;
    width: 100%;
  }

  * {
    box-sizing: border-box;
  }
  
  fieldset {
    border: none;
    padding: 0;
    margin: 0;
  }
  
  .skio-plan-picker {
    display: flex;
    flex-direction: column;
    gap: 12px;
    width: 100%;
  }
  
  /* Purchase option cards */
  .skio-choice {
    display: flex;
    flex-direction: column;
    padding: 20px 24px;
    border: 2px solid #e8e8e8;
    border-radius: 16px;
    position: relative;
    cursor: pointer;
    background-color: #fff;
    transition: all 0.2s ease;
  }

  .skio-choice:hover {
    border-color: #ccc;
  }
  
  .skio-choice--selected {
    border-color: #0a0a0a;
    background-color: #fafafa;
  }
  
  /* Hidden native radio */
  .skio-choice input[type='radio'] {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }
  
  /* Custom radio */
  .skio-radio {
    width: 20px;
    height: 20px;
    min-width: 20px;
    min-height: 20px;
    border: 2px solid #ccc;
    border-radius: 50%;
    margin-right: 14px;
    position: relative;
    flex-shrink: 0;
    transition: border-color 0.2s ease;
  }
  
  .skio-choice--selected .skio-radio {
    border-color: #0a0a0a;
  }
  
  .skio-radio::after {
    content: '';
    position: absolute;
    width: 10px;
    height: 10px;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    border-radius: 50%;
    background: transparent;
    transition: background 0.2s ease;
  }
  
  .skio-choice--selected .skio-radio::after {
    background: #0a0a0a;
  }
  
  /* Top line with label and price */
  .skio-topline {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  
  .skio-topline-left {
    display: flex;
    align-items: center;
  }
  
  .skio-label {
    font-size: 15px;
    font-weight: 600;
    color: #0a0a0a;
    margin: 0;
  }
  
  .skio-desc {
    font-size: 13px;
    color: #888;
    margin-top: 2px;
  }
  
  /* Pricing */
  .skio-pricing {
    text-align: right;
  }
  
  .skio-price {
    font-size: 17px;
    font-weight: 700;
    color: #0a0a0a;
  }
  
  .skio-price--sale {
    color: #2d5a27;
  }
  
  .skio-price--compare {
    font-size: 14px;
    text-decoration: line-through;
    color: #bbb;
    margin-left: 8px;
    font-weight: 400;
  }
  
  .skio-savings {
    font-size: 12px;
    color: #2d5a27;
    font-weight: 600;
  }
  
  /* Frequency dropdown */
  .skio-frequency-wrap {
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px solid #e8e8e8;
  }
  
  .skio-frequency-label {
    font-size: 13px;
    font-weight: 600;
    color: #0a0a0a;
    margin-bottom: 8px;
    display: block;
  }
  
  .skio-frequency {
    width: 100%;
    padding: 12px 16px;
    font-size: 14px;
    font-family: 'DM Sans', system-ui, sans-serif;
    border: 2px solid #e8e8e8;
    border-radius: 12px;
    background-color: #fff;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23666' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 16px center;
    appearance: none;
    cursor: pointer;
    transition: border-color 0.2s ease;
    min-height: 44px;
  }
  
  .skio-frequency:hover {
    border-color: #ccc;
  }
  
  .skio-frequency:focus {
    outline: 3px solid #005fcc;
    outline-offset: 2px;
  }
  
  /* Benefits list */
  .skio-benefits {
    list-style: none;
    padding: 0;
    margin: 16px 0 0;
    font-size: 13px;
    color: #666;
    line-height: 1.5;
  }
  
  .skio-benefits li {
    display: flex;
    align-items: center;
    margin-bottom: 6px;
  }
  
  .skio-benefits li:last-child {
    margin-bottom: 0;
  }
  
  .skio-check {
    color: #2d5a27;
    font-size: 14px;
    margin-right: 8px;
    font-weight: 600;
  }

  /* Focus visible for keyboard nav */
  .skio-choice:focus-within {
    outline: 3px solid #005fcc;
    outline-offset: 2px;
  }

  @media (forced-colors: active) {
    .skio-choice--selected {
      outline: 2px solid CanvasText;
    }
    
    .skio-radio {
      forced-color-adjust: none;
    }
  }
`;

export class SkioPlanPickerComponent extends LitElement {
  static properties = {
    product: { type: Object },
    selectedVariant: { type: Object },
    key: { type: String },
    formId: { type: String },
    skioSellingPlanGroups: { state: true },
    availableSellingPlanGroups: { state: true },
    selectedSellingPlanGroup: { state: true },
    selectedSellingPlan: { state: true },
    startSubscription: { type: Boolean },
    benefitVariant: { type: String }
  };

  static styles = skioStyles;

  constructor() {
    super();
    this.product = null;
    this.selectedVariant = null;
    this.key = null;
    this.formId = null;
    this.skioSellingPlanGroups = [];
    this.availableSellingPlanGroups = [];
    this.selectedSellingPlanGroup = null;
    this.selectedSellingPlan = null;
    this.startSubscription = true;
    this.benefitVariant = 'default';

    this.moneyFormatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: window?.Shopify?.currency?.active || 'USD'
    });
  }

  connectedCallback() {
    super.connectedCallback();
    this.detectBenefitVariant();
  }

  detectBenefitVariant() {
    if (!this.product?.id) return;
    
    const productId = parseInt(this.product.id);
    const frotherProductIds = [1765222776898, 8448802128060, 8448801046716];
    
    if (frotherProductIds.includes(productId)) {
      this.benefitVariant = 'frother';
    }
  }

  render() {
    // Don't render until we have product and variant data
    if (!this.product || !this.selectedVariant) {
      return html``;
    }

    const subscriptionAvailable = this.availableSellingPlanGroups?.length > 0;
    const defaultGroup = subscriptionAvailable ? this.availableSellingPlanGroups[0] : null;
    const defaultPlan = defaultGroup?.selling_plans?.[0] || null;
    
    const oneTimePrice = this.money(this.selectedVariant.price);
    const subscriptionPrice = defaultPlan ? this.price(defaultPlan) : oneTimePrice;
    const subscriptionDiscount = defaultPlan ? this.discount(defaultPlan) : { percent: '0%', amount: 0 };

    const isOneTime = !this.selectedSellingPlanGroup;
    const isSubscription = !!this.selectedSellingPlanGroup;

    const currentDiscount = this.selectedSellingPlan 
      ? this.discount(this.selectedSellingPlan)
      : subscriptionDiscount;

    return html`
      <fieldset class="skio-plan-picker" aria-label="Purchase options">
        <input type="hidden" name="selling_plan" value="${this.selectedSellingPlan?.id || ''}">
        <input 
          type="hidden" 
          name="properties[Discount]" 
          value="${this.selectedSellingPlan ? currentDiscount.percent : ''}"
          ?disabled="${!this.selectedSellingPlan}"
        >

        <!-- One-time purchase -->
        <div 
          class="skio-choice ${isOneTime ? 'skio-choice--selected' : ''}"
          @click="${() => this.selectGroup(null)}"
        >
          <input
            type="radio"
            id="skio-onetime-${this.key}"
            name="skio-purchase-${this.key}"
            value=""
            .checked="${isOneTime}"
            @change="${() => this.selectGroup(null)}"
          >
          <div class="skio-topline">
            <div class="skio-topline-left">
              <div class="skio-radio" aria-hidden="true"></div>
              <div>
                <p class="skio-label">One-time purchase</p>
              </div>
            </div>
            <div class="skio-pricing">
              <span class="skio-price">${oneTimePrice}</span>
            </div>
          </div>
        </div>

        <!-- Subscription -->
        ${subscriptionAvailable ? html`
          <div 
            class="skio-choice ${isSubscription ? 'skio-choice--selected' : ''}"
            @click="${() => this.selectGroup(defaultGroup)}"
          >
            <input
              type="radio"
              id="skio-subscribe-${this.key}"
              name="skio-purchase-${this.key}"
              value="${defaultGroup?.id || ''}"
              .checked="${isSubscription}"
              @change="${() => this.selectGroup(defaultGroup)}"
            >
            <div class="skio-topline">
              <div class="skio-topline-left">
                <div class="skio-radio" aria-hidden="true"></div>
                <div>
                  <p class="skio-label">Subscribe & Save</p>
                  <p class="skio-desc">Delivery every 30, 60, or 90 days</p>
                </div>
              </div>
              <div class="skio-pricing">
                <span class="skio-price skio-price--sale">${subscriptionPrice}</span>
                <span class="skio-price--compare">${oneTimePrice}</span>
                <p class="skio-savings">Save ${subscriptionDiscount.percent}</p>
              </div>
            </div>

            ${isSubscription ? html`
              <div class="skio-frequency-wrap">
                <label for="skio-freq-${this.key}" class="skio-frequency-label">Delivery frequency</label>
                <select 
                  id="skio-freq-${this.key}"
                  class="skio-frequency"
                  @change="${this.onFrequencyChange}"
                >
                  ${this.selectedSellingPlanGroup?.selling_plans?.map(plan => html`
                    <option 
                      value="${plan.id}"
                      ?selected="${this.selectedSellingPlan?.id === plan.id}"
                    >
                      ${plan.name}
                    </option>
                  `)}
                </select>
              </div>

              <ul class="skio-benefits">
                <li><span class="skio-check">✓</span> ${currentDiscount.percent} off every order</li>
                ${this.benefitVariant === 'frother' ? html`
                  <li><span class="skio-check">✓</span> FREE KOS Handheld Mixer Frother</li>
                ` : null}
                <li><span class="skio-check">✓</span> Access to exclusive offers</li>
                <li><span class="skio-check">✓</span> Pause or cancel anytime</li>
              </ul>
            ` : null}
          </div>
        ` : null}
      </fieldset>
    `;
  }

  updated(changed) {
    if (changed.has('product') && this.product) {
      this.key = this.product.id;
      this.skioSellingPlanGroups = (this.product.selling_plan_groups || []).filter(
        spg => spg.app_id === 'SKIO'
      );
      this.detectBenefitVariant();
    }

    if (changed.has('selectedVariant') && this.selectedVariant && this.skioSellingPlanGroups) {
      this.availableSellingPlanGroups = this.skioSellingPlanGroups
        .filter(spg =>
          spg.selling_plans?.some(sp =>
            this.selectedVariant.selling_plan_allocations?.some(
              spa => spa.selling_plan_id === sp.id
            )
          )
        )
        .map(group => ({
          ...group,
          selling_plans: group.selling_plans.filter(plan => !HIDDEN_SELLING_PLAN_IDS.includes(plan.id))
        }))
        .filter(group => group.selling_plans.length > 0);

      // Set default selection
      if (this.availableSellingPlanGroups.length > 0) {
        this.availableSellingPlanGroups.forEach(g => {
          g.selected_selling_plan = g.selling_plans?.[0];
        });

        if (this.startSubscription || this.product?.requires_selling_plan) {
          this.selectedSellingPlanGroup = this.availableSellingPlanGroups[0];
          this.selectedSellingPlan = this.selectedSellingPlanGroup?.selling_plans?.[0] || null;
        }
      }
    }

    if (changed.has('selectedSellingPlan')) {
      this.syncForm();
      this.dispatchUpdate();
    }
  }

  selectGroup(group) {
    if (!group) {
      this.selectedSellingPlanGroup = null;
      this.selectedSellingPlan = null;
    } else {
      this.selectedSellingPlanGroup = group;
      this.selectedSellingPlan = group.selected_selling_plan || group.selling_plans?.[0] || null;
    }
  }

  onFrequencyChange(e) {
    const planId = e.target.value;
    const plan = this.selectedSellingPlanGroup?.selling_plans?.find(p => p.id == planId);
    
    if (plan) {
      this.selectedSellingPlanGroup.selected_selling_plan = plan;
      this.selectedSellingPlan = plan;
    }
  }

  money(cents) {
    if (cents == null) return '$0';
    const formatted = this.moneyFormatter.format(cents / 100);
    return formatted.replace(/\.00$/, '');
  }

  discount(plan) {
    if (!plan || !this.selectedVariant) {
      return { percent: '0%', amount: 0 };
    }

    const price = this.selectedVariant.price;
    const adj = plan.price_adjustments?.[0];
    
    if (!adj) return { percent: '0%', amount: 0 };

    let discount = { percent: '0%', amount: 0 };

    switch (adj.value_type) {
      case 'percentage':
        discount.percent = `${adj.value}%`;
        discount.amount = Math.round((price * adj.value) / 100);
        break;
      case 'fixed_amount':
        discount.percent = `${Math.round((adj.value / price) * 100)}%`;
        discount.amount = adj.value;
        break;
      case 'price':
        const isPrepaid = plan.name?.toLowerCase().includes('prepaid');
        const adjustedPrice = isPrepaid ? adj.value / parseFloat(plan.name) : adj.value;
        discount.percent = `${Math.round(((price - adjustedPrice) / price) * 100)}%`;
        discount.amount = price - adjustedPrice;
        break;
    }

    return discount;
  }

  price(plan, formatted = true) {
    if (!plan || !this.selectedVariant) {
      return formatted ? this.money(this.selectedVariant?.price || 0) : (this.selectedVariant?.price || 0);
    }

    const discountAmount = this.discount(plan).amount;
    const newPrice = this.selectedVariant.price - discountAmount;
    return formatted ? this.money(newPrice) : newPrice;
  }

  syncForm() {
    if (!this.formId) return;

    const form = document.getElementById(this.formId);
    if (!form) return;

    let sellingPlanInput = form.querySelector('input[name="selling_plan"]');
    if (!sellingPlanInput) {
      sellingPlanInput = document.createElement('input');
      sellingPlanInput.type = 'hidden';
      sellingPlanInput.name = 'selling_plan';
      form.appendChild(sellingPlanInput);
    }
    sellingPlanInput.value = this.selectedSellingPlan?.id || '';

    let discountInput = form.querySelector('input[name="properties[Discount]"]');
    if (!discountInput) {
      discountInput = document.createElement('input');
      discountInput.type = 'hidden';
      discountInput.name = 'properties[Discount]';
      form.appendChild(discountInput);
    }
    discountInput.value = this.selectedSellingPlan ? this.discount(this.selectedSellingPlan).percent : '';
    discountInput.disabled = !this.selectedSellingPlan;
  }

  dispatchUpdate() {
    this.dispatchEvent(new CustomEvent('skio::update-selling-plan', {
      bubbles: true,
      composed: true,
      detail: {
        sellingPlan: this.selectedSellingPlan,
        salePrice: this.price(this.selectedSellingPlan),
        regularPrice: this.money(this.selectedVariant?.price),
        showSalePrice: this.selectedSellingPlan !== null
      }
    }));
  }
}

customElements.define('skio-plan-picker', SkioPlanPickerComponent);